import { Link } from 'react-router-dom';

import { DataTable, type Column } from '../components/DataTable.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { dateOnly, money, statusClass, statusLabel } from '../lib/format.ts';
import { usePaginatedList } from '../lib/usePaginatedList.ts';

interface InvoiceRow {
  id: number;
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string | null;
  customerNo: string | null;
  customerName: string | null;
  salesman: string | null;
  currency: string;
  value: number;
  outstanding: number | null;
  status: string;
}

export function InvoicesListPage() {
  const list = usePaginatedList<InvoiceRow>({
    endpoint: '/api/dashboard/invoices',
    defaultSortColumn: 'invoice_date',
    defaultSortDirection: 'DESC',
    filterKeys: ['status', 'outstandingOnly', 'customerNo'],
  });

  const columns: Column<InvoiceRow>[] = [
    {
      key: 'invoice_no',
      label: 'Invoice',
      sortable: true,
      className: 'mono',
      render: (r) => (
        <Link to={`/invoices/${encodeURIComponent(r.invoiceNo)}`}>{r.invoiceNo}</Link>
      ),
    },
    {
      key: 'invoice_date',
      label: 'Date',
      sortable: true,
      className: 'mono',
      render: (r) => dateOnly(r.invoiceDate),
    },
    {
      key: 'customer_no',
      label: 'Customer',
      sortable: true,
      render: (r) =>
        r.customerNo ? (
          <Link to={`/customers/${encodeURIComponent(r.customerNo)}`}>
            {r.customerName ?? r.customerNo}
          </Link>
        ) : (
          '—'
        ),
    },
    { key: 'salesman', label: 'Salesman', render: (r) => r.salesman ?? '—' },
    {
      key: 'value',
      label: 'Value',
      sortable: true,
      className: 'num',
      render: (r) => money(r.value, r.currency),
    },
    {
      key: 'outstanding',
      label: 'Outstanding',
      sortable: true,
      className: 'num',
      render: (r) => money(r.outstanding, r.currency),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (r) => <span className={`badge ${statusClass(r.status)}`}>{statusLabel(r.status)}</span>,
    },
  ];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Invoices</h1>
        <span className="main-subtitle">{list.data ? `${list.data.total} total` : ''}</span>
      </header>

      <div className="toolbar">
        <input
          placeholder="Search invoice no or ref…"
          defaultValue={list.state.search}
          onChange={(e) => list.setSearch(e.target.value)}
        />
        <select
          value={list.state.filters.status ?? ''}
          onChange={(e) => list.setFilter('status', e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="N">Active</option>
          <option value="O">Open</option>
          <option value="C">Closed</option>
          <option value="X">Cancelled</option>
          <option value="A">Pending</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={list.state.filters.outstandingOnly === '1'}
            onChange={(e) =>
              list.setFilter('outstandingOnly', e.target.checked ? '1' : '')
            }
            style={{ width: 'auto', minWidth: 0 }}
          />
          Outstanding only
        </label>
      </div>

      {list.error && <div className="error">{(list.error as Error).message}</div>}
      {list.isLoading && !list.data && <div className="loading">Loading…</div>}

      {list.data && (
        <>
          <DataTable
            columns={columns}
            rows={list.data.rows}
            rowKey={(r) => r.id}
            sortColumn={list.state.sortColumn}
            sortDirection={list.state.sortDirection}
            onSortChange={list.toggleSort}
          />
          <Pagination
            page={list.state.page}
            pageSize={list.state.pageSize}
            total={list.data.total}
            onPageChange={list.setPage}
          />
        </>
      )}
    </>
  );
}
